/**
 * HospitalLayout — Reception-friendly layout for Hospital Portal.
 * Header shows portal name, role, and an Internal Testing badge for full-access users.
 */
import { Outlet, useNavigate } from 'react-router-dom';
import { Building2, LogOut, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useHospitalAuth } from '../hooks/useHospitalAuth';
import InlineErrorBoundary from '@/components/system/InlineErrorBoundary';
import { portalPath } from '@/shared/app/portalPath';
import { SuperAdminBadge } from '@/shared/components/SuperAdminBadge';
import { PortalAccessGate } from '@/shared/components/PortalAccessGate';

export default function HospitalLayout() {
  const navigate = useNavigate();
  const { profile } = useHospitalAuth();
  const p = portalPath('hospital');

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate(p('/login'));
  };

  const roleLabel = profile?.role === 'hospital_master' ? 'Hospital Master' : 'Hospital Staff';
  const showInternal = !!profile?.canAccessAllHospitals;

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 sm:px-6 py-3.5 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-teal-600 flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-bold text-foreground leading-tight">Hospital Portal</h1>
              <SuperAdminBadge portal="hospital_portal" />
              {showInternal && (
                <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">
                  <ShieldCheck className="h-3 w-3" /> Internal Testing
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {roleLabel}
              {!showInternal && profile?.hospitalName ? ` • ${profile.hospitalName}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {profile?.fullName && (
            <span className="text-xs text-muted-foreground hidden sm:inline">{profile.fullName}</span>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-1.5 text-muted-foreground rounded-xl">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline text-sm">Logout</span>
          </Button>
        </div>
      </header>

      <main className="flex-1 p-3 sm:p-5 lg:p-6 max-w-7xl mx-auto w-full">
        <InlineErrorBoundary title="Hospital page crashed">
          <PortalAccessGate portal="hospital_portal"><Outlet /></PortalAccessGate>
        </InlineErrorBoundary>
      </main>
    </div>
  );
}
